import { API } from '@pibex/shared';
import type { InvitationResponse } from '@pibex/shared';
import { useId, useState } from 'react';
import type { FormEvent } from 'react';
import { Button } from '../../components/Button.js';
import { Notice } from '../../components/Notice.js';
import { es } from '../../i18n/es.js';
import { api, isApiError } from '../../lib/api.js';

type Feedback = { tone: 'success' | 'error' | 'warning'; text: string } | null;

interface InviteFormProps {
  onInvited(): Promise<void> | void;
}

export function InviteForm({ onInvited }: InviteFormProps) {
  const id = useId();
  const [email, setEmail] = useState('');
  const [pending, setPending] = useState(false);
  const [feedback, setFeedback] = useState<Feedback>(null);
  const t = es.adminPage.invite;

  const submit = async (event: FormEvent<HTMLFormElement>) => {
    event.preventDefault();
    const value = email.trim().toLowerCase();
    if (!value) return;
    setPending(true);
    setFeedback(null);
    try {
      const { invitation } = await api.post<InvitationResponse>(API.adminInvitations, {
        email: value,
      });
      setEmail('');
      setFeedback(
        invitation.emailError
          ? { tone: 'warning', text: t.successNoEmail(invitation.email) }
          : { tone: 'success', text: t.success(invitation.email) },
      );
      await onInvited();
    } catch (err) {
      if (isApiError(err, 'conflict')) setFeedback({ tone: 'error', text: t.conflict });
      else if (isApiError(err, 'validation')) setFeedback({ tone: 'error', text: t.validation });
      else if (isApiError(err)) setFeedback({ tone: 'error', text: es.genericError });
      else setFeedback({ tone: 'error', text: es.networkError });
    } finally {
      setPending(false);
    }
  };

  return (
    <section aria-labelledby={`${id}-heading`} className="flex flex-col gap-3">
      <h2 id={`${id}-heading`} className="text-lg font-semibold tracking-tight">
        {t.heading}
      </h2>
      <form onSubmit={(e) => void submit(e)} className="flex flex-col gap-3" noValidate>
        <label htmlFor={`${id}-email`} className="text-sm font-medium">
          {t.label}
        </label>
        <div className="flex flex-col gap-2 sm:flex-row">
          <input
            id={`${id}-email`}
            name="email"
            type="email"
            inputMode="email"
            autoComplete="off"
            autoCapitalize="none"
            spellCheck={false}
            required
            placeholder={t.placeholder}
            value={email}
            onChange={(e) => setEmail(e.target.value)}
            disabled={pending}
            aria-describedby={`${id}-hint`}
            className="min-h-11 flex-1 rounded-md border border-neutral-300 bg-white px-3 text-base text-neutral-900 placeholder:text-neutral-400 focus-visible:border-accent focus-visible:outline-2 focus-visible:outline-offset-0 focus-visible:outline-accent disabled:opacity-60 dark:border-neutral-700 dark:bg-neutral-900 dark:text-neutral-100 dark:placeholder:text-neutral-500"
          />
          <Button type="submit" disabled={pending || email.trim() === ''} className="sm:min-w-28">
            {pending ? t.submitting : t.submit}
          </Button>
        </div>
        <p id={`${id}-hint`} className="text-sm text-neutral-500 dark:text-neutral-400">
          {t.hint}
        </p>
        <div aria-live="polite" className="min-h-0">
          {feedback ? <Notice tone={feedback.tone}>{feedback.text}</Notice> : null}
        </div>
      </form>
    </section>
  );
}
